import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FaPlus, FaEdit, FaCalendarAlt, FaList, FaCog } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

interface AcademicYear {
    id: number;
    name: string;
    start_date: string;
    end_date: string;
    is_active: boolean;
}

interface Term {
    id: number;
    name: string;
    academic_year: number;
    academic_year_name: string;
    start_date: string;
    end_date: string;
    is_active: boolean;
}

interface AssessmentType {
    id: number;
    name: string;
    code: string;
}

interface TermAssessment {
    id: number;
    term: number;
    term_name: string;
    assessment_type: number;
    assessment_type_name: string;
    max_marks: number;
    weight: number;
}

const ExamConfiguration: React.FC = () => {
    const [activeTab, setActiveTab] = useState<'years' | 'terms' | 'assessments'>('years');
    const [years, setYears] = useState<AcademicYear[]>([]);
    const [terms, setTerms] = useState<Term[]>([]);
    const [assessmentTypes, setAssessmentTypes] = useState<AssessmentType[]>([]);
    const [termAssessments, setTermAssessments] = useState<TermAssessment[]>([]);
    const [selectedTerm, setSelectedTerm] = useState<number | null>(null);

    // Loading states
    const [loading, setLoading] = useState(false);

    // Modal States
    const [showYearModal, setShowYearModal] = useState(false);
    const [showTermModal, setShowTermModal] = useState(false);
    const [showAssessmentModal, setShowAssessmentModal] = useState(false);

    // Form States
    const [yearForm, setYearForm] = useState({ name: '', start_date: '', end_date: '', is_active: true });
    const [termForm, setTermForm] = useState({ name: '', academic_year: '', start_date: '', end_date: '', is_active: true });
    const [assessmentForm, setAssessmentForm] = useState({ assessment_type: '', max_marks: 30, weight: 30 }); // Default CAT values

    const [saving, setSaving] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            if (activeTab === 'years') {
                const res = await axios.get('/exams/academic-years/');
                setYears(res.data);
            } else if (activeTab === 'terms') {
                const res = await axios.get('/exams/terms/');
                setTerms(res.data);
                const yearsRes = await axios.get('/exams/academic-years/');
                setYears(yearsRes.data);
            } else {
                const typesRes = await axios.get('/exams/assessment-types/');
                setAssessmentTypes(typesRes.data);
                const termsRes = await axios.get('/exams/terms/');
                setTerms(termsRes.data);

                // Auto-select first active term or any term
                if (!selectedTerm && termsRes.data.length > 0) {
                    const active = termsRes.data.find((t: any) => t.is_active);
                    setSelectedTerm(active ? active.id : termsRes.data[0].id);
                }
            }
        } catch (error) {
            toast.error("Failed to load data");
        } finally {
            setLoading(false);
        }
    }, [activeTab, selectedTerm]);

    useEffect(() => {
        fetchData();
    }, [activeTab, fetchData]);

    useEffect(() => {
        if (activeTab === 'assessments' && selectedTerm) {
            fetchTermAssessments(selectedTerm);
        }
    }, [selectedTerm, activeTab]);

    const fetchTermAssessments = async (termId: number) => {
        try {
            const res = await axios.get(`/exams/term-assessments/?term_id=${termId}`);
            setTermAssessments(res.data);
        } catch (err) {
            console.error(err);
        }
    }

    // --- Handlers ---

    const handleCreateYear = async () => {
        if (!yearForm.name || !yearForm.start_date || !yearForm.end_date) {
            toast.warn("Fadlan buuxi dhammaan meelaha"); return;
        }
        setSaving(true);
        try {
            await axios.post('/exams/academic-years/', yearForm);
            toast.success("Sannad Dugsiyeedka La Abuuray!");
            setShowYearModal(false);
            fetchData(); // Refresh list
        } catch (err: any) {
            toast.error(err.response?.data?.detail || "Ku guuldareystay abuurista sannadka");
        } finally { setSaving(false); }
    };

    const handleCreateTerm = async () => {
        if (!termForm.name || !termForm.academic_year || !termForm.start_date || !termForm.end_date) {
            toast.warn("Fadlan buuxi dhammaan meelaha"); return;
        }
        setSaving(true);
        try {
            await axios.post('/exams/terms/', termForm);
            toast.success("Teermka La Abuuray!");
            setShowTermModal(false);
            fetchData(); // Refresh list
        } catch (err: any) {
            toast.error(err.response?.data?.detail || "Ku guuldareystay abuurista teermka");
        } finally { setSaving(false); }
    };

    const handleCreateAssessment = async () => {
        if (!selectedTerm || !assessmentForm.assessment_type) {
            toast.warn("Fadlan buuxi dhammaan meelaha"); return;
        }
        setSaving(true);
        try {
            await axios.post('/exams/term-assessments/', {
                term: selectedTerm,
                ...assessmentForm
            });
            toast.success("Qiimeynta Lagu Daray!");
            setShowAssessmentModal(false);
            fetchTermAssessments(selectedTerm); // Refresh list
        } catch (err: any) {
            toast.error(err.response?.data?.detail || "Ku guuldareystay in lagu daro qiimeynta");
        } finally { setSaving(false); }
    };


    return (
        <div className="dashboard-content">
            <ToastContainer />
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold text-dark mb-1">Habeynta Imtixaanada</h2>
                    <p className="text-secondary mb-0">Maamul Sannad Dugsiyeedyada, Teerm-yada, iyo Miisaanka Qiimeynta</p>
                </div>
            </div>

            {/* Modern Tabs */}
            <div className="bg-white rounded-3 shadow-sm p-1 d-inline-flex mb-4">
                <button onClick={() => setActiveTab('years')} className={`btn border-0 rounded-2 px-4 py-2 fw-medium d-flex align-items-center ${activeTab === 'years' ? 'bg-primary text-white shadow-sm' : 'text-secondary hover-bg-light'}`}>
                    <FaCalendarAlt className="me-2" /> Sannad Dugsiyeedyada
                </button>
                <button onClick={() => setActiveTab('terms')} className={`btn border-0 rounded-2 px-4 py-2 fw-medium d-flex align-items-center ${activeTab === 'terms' ? 'bg-primary text-white shadow-sm' : 'text-secondary hover-bg-light'}`}>
                    <FaList className="me-2" /> Teerm-yada
                </button>
                <button onClick={() => setActiveTab('assessments')} className={`btn border-0 rounded-2 px-4 py-2 fw-medium d-flex align-items-center ${activeTab === 'assessments' ? 'bg-primary text-white shadow-sm' : 'text-secondary hover-bg-light'}`}>
                    <FaCog className="me-2" /> Qiimeynta & Miisaamada
                </button>
            </div>

            {/* Content Area */}
            <div className="bg-white rounded-4 shadow-sm p-4 position-relative">

                {loading && <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>}

                {!loading && activeTab === 'years' && (
                    <div>
                        <div className="d-flex justify-content-between mb-3">
                            <h5 className="fw-bold">Sannad Dugsiyeedyada</h5>
                            <button className="btn btn-primary btn-sm" onClick={() => setShowYearModal(true)}><FaPlus className="me-2" /> Sannad Cusub</button>
                        </div>
                        <div className="table-responsive">
                            <table className="table table-hover align-middle">
                                <thead className="bg-light">
                                    <tr><th>Magaca</th><th>Taariikhda Bilowga</th><th>Taariikhda Dhamaadka</th><th>Xaaladda</th><th>Ficilada</th></tr>
                                </thead>
                                <tbody>
                                    {years.map(year => (
                                        <tr key={year.id}>
                                            <td className="fw-medium">{year.name}</td>
                                            <td>{year.start_date}</td>
                                            <td>{year.end_date}</td>
                                            <td>{year.is_active ? <span className="badge bg-success-subtle text-success px-3 py-2 rounded-pill">Firfircoon</span> : <span className="badge bg-secondary-subtle text-secondary px-3 py-2 rounded-pill">Aan Shaqeyn</span>}</td>
                                            <td><button className="btn btn-sm btn-icon text-primary"><FaEdit /></button></td>
                                        </tr>
                                    ))}
                                    {years.length === 0 && <tr><td colSpan={5} className="text-center py-4 text-muted">Lama helin sannad dugsiyeedyo la qeexay.</td></tr>}
                                </tbody>
                            </table>
                        </div>

                        {/* Year Modal Overlay */}
                        {showYearModal && (
                            <div className="modal-overlay">
                                <div className="modal-content-custom card p-4 shadow-lg border-0" style={{ maxWidth: '400px' }}>
                                    <h5 className="fw-bold mb-3">Ku Dar Sannad Dugsiyeed</h5>
                                    <div className="mb-3">
                                        <label className="form-label small fw-bold">Magaca Sannadka (tusaale 2024)</label>
                                        <input type="text" className="form-control" value={yearForm.name} onChange={e => setYearForm({ ...yearForm, name: e.target.value })} />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label small fw-bold">Taariikhda Bilowga</label>
                                        <input type="date" className="form-control" value={yearForm.start_date} onChange={e => setYearForm({ ...yearForm, start_date: e.target.value })} />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label small fw-bold">Taariikhda Dhamaadka</label>
                                        <input type="date" className="form-control" value={yearForm.end_date} onChange={e => setYearForm({ ...yearForm, end_date: e.target.value })} />
                                    </div>
                                    <div className="d-flex justify-content-end gap-2 mt-2">
                                        <button className="btn btn-light" onClick={() => setShowYearModal(false)}>Jooji</button>
                                        <button className="btn btn-primary" onClick={handleCreateYear} disabled={saving}>{saving ? 'Badbaadinaya...' : 'Abuur'}</button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {!loading && activeTab === 'terms' && (
                    <div>
                        <div className="d-flex justify-content-between mb-3">
                            <h5 className="fw-bold">Teerm-yada</h5>
                            <button className="btn btn-primary btn-sm" onClick={() => setShowTermModal(true)}><FaPlus className="me-2" /> Teerm Cusub</button>
                        </div>
                        <div className="table-responsive">
                            <table className="table table-hover align-middle">
                                <thead className="bg-light">
                                    <tr><th>Magaca Teermka</th><th>Sannad Dugsiyeedka</th><th>Taariikhaha</th><th>Xaaladda</th><th>Ficilada</th></tr>
                                </thead>
                                <tbody>
                                    {terms.map(term => (
                                        <tr key={term.id}>
                                            <td className="fw-medium">{term.name}</td>
                                            <td>{term.academic_year_name}</td>
                                            <td className="small text-muted">{term.start_date} - {term.end_date}</td>
                                            <td>{term.is_active ? <span className="badge bg-success-subtle text-success px-3 py-2 rounded-pill">Firfircoon</span> : <span className="badge bg-secondary-subtle text-secondary px-3 py-2 rounded-pill">Aan Shaqeyn</span>}</td>
                                            <td><button className="btn btn-sm btn-icon text-primary"><FaEdit /></button></td>
                                        </tr>
                                    ))}
                                    {terms.length === 0 && <tr><td colSpan={5} className="text-center py-4 text-muted">Lama helin teerm-yo la qeexay.</td></tr>}
                                </tbody>
                            </table>
                        </div>

                        {/* Term Modal Overlay */}
                        {showTermModal && (
                            <div className="modal-overlay">
                                <div className="modal-content-custom card p-4 shadow-lg border-0" style={{ maxWidth: '400px' }}>
                                    <h5 className="fw-bold mb-3">Ku Dar Teerm</h5>
                                    <div className="mb-3">
                                        <label className="form-label small fw-bold">Magaca Teermka (tusaale Teerm 1)</label>
                                        <input type="text" className="form-control" value={termForm.name} onChange={e => setTermForm({ ...termForm, name: e.target.value })} />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label small fw-bold">Sannad Dugsiyeedka</label>
                                        <select className="form-select" value={termForm.academic_year} onChange={e => setTermForm({ ...termForm, academic_year: e.target.value })}>
                                            <option value="">Dooro Sannad...</option>
                                            {years.map(y => <option key={y.id} value={y.id}>{y.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label small fw-bold">Taariikhda Bilowga</label>
                                        <input type="date" className="form-control" value={termForm.start_date} onChange={e => setTermForm({ ...termForm, start_date: e.target.value })} />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label small fw-bold">Taariikhda Dhamaadka</label>
                                        <input type="date" className="form-control" value={termForm.end_date} onChange={e => setTermForm({ ...termForm, end_date: e.target.value })} />
                                    </div>
                                    <div className="d-flex justify-content-end gap-2 mt-2">
                                        <button className="btn btn-light" onClick={() => setShowTermModal(false)}>Jooji</button>
                                        <button className="btn btn-primary" onClick={handleCreateTerm} disabled={saving}>{saving ? 'Badbaadinaya...' : 'Abuur'}</button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {!loading && activeTab === 'assessments' && (
                    <div>
                        <div className="row mb-4">
                            <div className="col-md-4">
                                <label className="form-label fw-bold">Dooro Teerm si aad u habeysid</label>
                                <select className="form-select" value={selectedTerm || ''} onChange={(e) => setSelectedTerm(parseInt(e.target.value))}>
                                    {terms.map(t => (
                                        <option key={t.id} value={t.id}>{t.name} ({t.academic_year_name})</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {selectedTerm && (
                            <>
                                <h5 className="fw-bold mb-3 border-bottom pb-2">Qiimeynta Teermka La Doortay</h5>
                                <div className="row g-3">
                                    {termAssessments.map(ta => (
                                        <div className="col-md-6" key={ta.id}>
                                            <div className="card h-100 border shadow-sm">
                                                <div className="card-body">
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <h6 className="fw-bold mb-0 text-primary">{ta.assessment_type_name}</h6>
                                                        <span className="badge bg-light text-dark border">Code: {ta.assessment_type_name.substring(0, 3).toUpperCase()}</span>
                                                    </div>
                                                    <div className="d-flex justify-content-between mt-3">
                                                        <div className="text-center flex-grow-1 border-end">
                                                            <div className="display-6 fw-bold text-dark">{ta.max_marks}</div>
                                                            <small className="text-secondary text-uppercase fw-bold" style={{ fontSize: '0.7rem' }}>Dhibcaha Ugu Sareeya</small>
                                                        </div>
                                                        <div className="text-center flex-grow-1">
                                                            <div className="display-6 fw-bold text-success">{ta.weight}%</div>
                                                            <small className="text-secondary text-uppercase fw-bold" style={{ fontSize: '0.7rem' }}>Miisaanka</small>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="card-footer bg-light border-0 d-flex justify-content-end p-2">
                                                    <button className="btn btn-sm btn-link text-decoration-none text-secondary">Isbedel Habeynta</button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                    {/* Add New Card */}
                                    <div className="col-md-6" onClick={() => setShowAssessmentModal(true)}>
                                        <div className="card h-100 border border-dashed border-2 bg-light d-flex align-items-center justify-content-center text-secondary" style={{ minHeight: '180px', cursor: 'pointer' }}>
                                            <div className="text-center">
                                                <FaPlus className="display-6 mb-2 opacity-50" />
                                                <div className="fw-medium">Ku Dar Nooca Qiimeynta</div>
                                                <small>(tusaale Project, Mid-Term)</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Assessment Modal Overlay */}
                        {showAssessmentModal && (
                            <div className="modal-overlay">
                                <div className="modal-content-custom card p-4 shadow-lg border-0" style={{ maxWidth: '400px' }}>
                                    <h5 className="fw-bold mb-3">Ku Dar Qiimeyn</h5>
                                    <div className="mb-3">
                                        <label className="form-label small fw-bold">Nooca Qiimeynta</label>
                                        <select className="form-select" value={assessmentForm.assessment_type} onChange={e => setAssessmentForm({ ...assessmentForm, assessment_type: e.target.value })}>
                                            <option value="">Dooro Nooca...</option>
                                            {assessmentTypes.map(at => <option key={at.id} value={at.id}>{at.name}</option>)}
                                        </select>
                                        <div className="form-text small">Standard types: CAT, FINAL, OPENER</div>
                                    </div>
                                    <div className="row">
                                        <div className="col-6 mb-3">
                                            <label className="form-label small fw-bold">Dhibcaha Ugu Sareeya</label>
                                            <input type="number" className="form-control" value={assessmentForm.max_marks} onChange={e => setAssessmentForm({ ...assessmentForm, max_marks: parseInt(e.target.value) })} />
                                        </div>
                                        <div className="col-6 mb-3">
                                            <label className="form-label small fw-bold">Miisaanka (%)</label>
                                            <input type="number" className="form-control" value={assessmentForm.weight} onChange={e => setAssessmentForm({ ...assessmentForm, weight: parseInt(e.target.value) })} />
                                        </div>
                                    </div>
                                    <div className="alert alert-info py-2 small">
                                        Hada la isticmaalay: {termAssessments.reduce((acc, curr) => acc + curr.weight, 0)}%.
                                        Hadhaaga: {100 - termAssessments.reduce((acc, curr) => acc + curr.weight, 0)}%.
                                    </div>

                                    <div className="d-flex justify-content-end gap-2 mt-2">
                                        <button className="btn btn-light" onClick={() => setShowAssessmentModal(false)}>Jooji</button>
                                        <button className="btn btn-primary" onClick={handleCreateAssessment} disabled={saving}>{saving ? 'Badbaadinaya...' : 'Ku Dar'}</button>
                                    </div>
                                </div>
                            </div>
                        )}

                    </div>
                )}

            </div>

            <style>{`
        .hover-bg-light:hover { background-color: #f1f5f9; }
        .btn-icon { width: 32px; height: 32px; display: inline-flex; align-items: center; justify-content: center; border-radius: 50%; }
        .btn-icon:hover { background-color: #e0f2fe; }
        
        /* Simple Modal Overlay */
        .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background: rgba(0,0,0,0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 2000;
        }
        .modal-content-custom {
            width: 100%;
            animation: fadeIn 0.2s ease-out;
        }
        @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-20px); }
            to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
        </div>
    );
};

export default ExamConfiguration;
